/**
 * Vanguard permanent talent purchase / unlock rules (pure domain).
 * Source: VANGUARD_FINAL_SPEC.md. Combat effect application is M6-B — deferred.
 */

import {
  VANGUARD_TALENT_GATES,
  VANGUARD_TALENT_NODES,
  getVanguardTalentNode,
  listVanguardTalentNodes,
  type TalentNodeDef,
  type TalentTier,
  type VanguardBranchId,
} from '../data/vanguardTalents';
import type { ClassProgress, PermanentSave } from '../persistence/schema';

/** Persisted ranks by node id. Missing key = 0 ranks. */
export type TalentRankMap = Record<string, number>;

export type TalentNodeVisualState =
  | 'locked'
  | 'available'
  | 'purchased'
  | 'maxed';

export interface BranchPointSummary {
  branch: VanguardBranchId;
  pointsSpent: number;
  unlockedTiers: TalentTier[];
  specialisationOpen: boolean;
}

export interface TalentPurchasePreview {
  nodeId: string;
  currentRanks: number;
  maxRanks: number;
  ranksRequested: number;
  ranksAffordable: number;
  cost: number;
  unspentBefore: number;
  unspentAfter: number;
  tierUnlocked: boolean;
  branchOpen: boolean;
  canPurchase: boolean;
  rejectReason: string | null;
}

export type TalentPurchaseRejectReason =
  | 'unknown_node'
  | 'insufficient_points'
  | 'tier_locked'
  | 'branch_locked'
  | 'already_maxed'
  | 'invalid_ranks'
  | 'over_spent_inconsistent';

export class TalentPurchaseError extends Error {
  readonly reason: TalentPurchaseRejectReason;

  constructor(reason: TalentPurchaseRejectReason, message: string) {
    super(message);
    this.name = 'TalentPurchaseError';
    this.reason = reason;
  }
}

export function emptyTalentRanks(): TalentRankMap {
  return {};
}

export function normalizeTalentRanks(raw: unknown): TalentRankMap {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return emptyTalentRanks();
  }
  const result: TalentRankMap = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!NODE_ID_SET.has(id)) {
      continue;
    }
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      continue;
    }
    const node = getVanguardTalentNode(id);
    const ranks = Math.max(0, Math.min(node.maxRanks, Math.floor(value)));
    if (ranks > 0) {
      result[id] = ranks;
    }
  }
  return result;
}

const NODE_ID_SET = new Set(VANGUARD_TALENT_NODES.map((n) => n.id));

export function ranksOf(ranks: TalentRankMap, nodeId: string): number {
  return ranks[nodeId] ?? 0;
}

export function totalSpentFromRanks(ranks: TalentRankMap): number {
  let total = 0;
  for (const [id, value] of Object.entries(ranks)) {
    if (!NODE_ID_SET.has(id)) continue;
    total += Math.max(0, Math.floor(value));
  }
  return total;
}

export function pointsSpentInBranch(
  ranks: TalentRankMap,
  branch: VanguardBranchId,
): number {
  let total = 0;
  for (const node of listVanguardTalentNodes(branch)) {
    total += ranksOf(ranks, node.id);
  }
  return total;
}

export function unspentClassTalentPoints(cls: ClassProgress): number {
  return Math.max(0, cls.earnedPoints - cls.spentPoints);
}

/**
 * Available spendable points including granted achievement points.
 * Achievement grants stay 0 until designed (reservedAchievementPoints field).
 */
export function availableClassTalentPoints(cls: ClassProgress): number {
  const granted =
    cls.earnedPoints + Math.max(0, cls.reservedAchievementPoints);
  return Math.max(0, granted - cls.spentPoints);
}

export function isSpecialisationOpen(ranks: TalentRankMap): boolean {
  return (
    pointsSpentInBranch(ranks, 'general') >=
    VANGUARD_TALENT_GATES.specialisationsAtGeneralPoints
  );
}

export function unlockedTiersForBranch(
  ranks: TalentRankMap,
  branch: VanguardBranchId,
): TalentTier[] {
  const generalPoints = pointsSpentInBranch(ranks, 'general');
  if (branch === 'general') {
    const tiers: TalentTier[] = [1];
    if (generalPoints >= VANGUARD_TALENT_GATES.generalTier2At) {
      tiers.push(2);
    }
    if (generalPoints >= VANGUARD_TALENT_GATES.generalTier3At) {
      tiers.push(3);
    }
    return tiers;
  }

  if (generalPoints < VANGUARD_TALENT_GATES.specialisationsAtGeneralPoints) {
    return [];
  }

  const branchPoints = pointsSpentInBranch(ranks, branch);
  const tiers: TalentTier[] = [1];
  if (branchPoints >= VANGUARD_TALENT_GATES.specTier2At) {
    tiers.push(2);
  }
  if (branchPoints >= VANGUARD_TALENT_GATES.specTier3At) {
    tiers.push(3);
  }
  return tiers;
}

export function isTierUnlocked(
  ranks: TalentRankMap,
  branch: VanguardBranchId,
  tier: TalentTier,
): boolean {
  return unlockedTiersForBranch(ranks, branch).includes(tier);
}

export function isBranchOpen(
  ranks: TalentRankMap,
  branch: VanguardBranchId,
): boolean {
  if (branch === 'general') {
    return true;
  }
  return isSpecialisationOpen(ranks);
}

/**
 * Decorative connecting lines never gate purchases — only branch/tier rules.
 */
export function canPurchaseNodeTier(
  ranks: TalentRankMap,
  node: TalentNodeDef,
): boolean {
  return (
    isBranchOpen(ranks, node.branch) &&
    isTierUnlocked(ranks, node.branch, node.tier)
  );
}

export function visualStateForNode(
  ranks: TalentRankMap,
  node: TalentNodeDef,
  unspent: number,
): TalentNodeVisualState {
  const current = ranksOf(ranks, node.id);
  if (current >= node.maxRanks) {
    return 'maxed';
  }
  if (!canPurchaseNodeTier(ranks, node)) {
    return 'locked';
  }
  if (current > 0) {
    return unspent > 0 ? 'purchased' : 'purchased';
  }
  return unspent > 0 ? 'available' : 'locked';
}

export function summarizeBranches(ranks: TalentRankMap): BranchPointSummary[] {
  const specsOpen = isSpecialisationOpen(ranks);
  return (['general', 'berserker', 'skirmisher', 'bulwark'] as const).map(
    (branch) => ({
      branch,
      pointsSpent: pointsSpentInBranch(ranks, branch),
      unlockedTiers: unlockedTiersForBranch(ranks, branch),
      specialisationOpen: branch === 'general' ? true : specsOpen,
    }),
  );
}

/**
 * Preview a multi-rank purchase without mutating state.
 * ranksToBuy defaults to 1; pass more for atomic multi-rank spend.
 */
export function previewTalentPurchase(
  cls: ClassProgress,
  nodeId: string,
  ranksToBuy = 1,
): TalentPurchasePreview {
  const unspent = availableClassTalentPoints(cls);
  const ranks = cls.talentRanks ?? emptyTalentRanks();

  let node: TalentNodeDef;
  try {
    node = getVanguardTalentNode(nodeId);
  } catch {
    return {
      nodeId,
      currentRanks: 0,
      maxRanks: 0,
      ranksRequested: ranksToBuy,
      ranksAffordable: 0,
      cost: 0,
      unspentBefore: unspent,
      unspentAfter: unspent,
      tierUnlocked: false,
      branchOpen: false,
      canPurchase: false,
      rejectReason: 'unknown_node',
    };
  }

  const currentRanks = ranksOf(ranks, node.id);
  const branchOpen = isBranchOpen(ranks, node.branch);
  const tierUnlocked = canPurchaseNodeTier(ranks, node);
  const room = Math.max(0, node.maxRanks - currentRanks);
  const ranksAffordable = Math.min(room, Math.max(0, unspent));

  const fail = (
    rejectReason: TalentPurchaseRejectReason,
  ): TalentPurchasePreview => ({
    nodeId,
    currentRanks,
    maxRanks: node.maxRanks,
    ranksRequested: ranksToBuy,
    ranksAffordable,
    cost: 0,
    unspentBefore: unspent,
    unspentAfter: unspent,
    tierUnlocked,
    branchOpen,
    canPurchase: false,
    rejectReason,
  });

  if (!Number.isInteger(ranksToBuy) || ranksToBuy <= 0) {
    return fail('invalid_ranks');
  }
  if (!branchOpen) {
    return fail('branch_locked');
  }
  if (!tierUnlocked) {
    return fail('tier_locked');
  }
  if (room <= 0) {
    return fail('already_maxed');
  }
  // Atomic multi-rank: must request within remaining room.
  if (ranksToBuy > room) {
    return fail('invalid_ranks');
  }
  if (unspent < ranksToBuy) {
    return fail('insufficient_points');
  }

  return {
    nodeId,
    currentRanks,
    maxRanks: node.maxRanks,
    ranksRequested: ranksToBuy,
    ranksAffordable: ranksToBuy,
    cost: ranksToBuy,
    unspentBefore: unspent,
    unspentAfter: unspent - ranksToBuy,
    tierUnlocked,
    branchOpen,
    canPurchase: true,
    rejectReason: null,
  };
}

/**
 * Atomically purchase ranks on one node. Validates branch/tier gates and points.
 * Does not apply combat effects (M6-B).
 */
export function purchaseTalentRanks(
  cls: ClassProgress,
  nodeId: string,
  ranksToBuy = 1,
): ClassProgress {
  const preview = previewTalentPurchase(cls, nodeId, ranksToBuy);
  if (!preview.canPurchase) {
    throw new TalentPurchaseError(
      (preview.rejectReason as TalentPurchaseRejectReason) ?? 'invalid_ranks',
      `Cannot purchase ${ranksToBuy} rank(s) of ${nodeId}: ${preview.rejectReason}`,
    );
  }

  const ranks = { ...(cls.talentRanks ?? emptyTalentRanks()) };
  const spentFromRanks = totalSpentFromRanks(ranks);
  if (spentFromRanks !== cls.spentPoints) {
    // Heal inconsistency toward ranks as source of truth before spend.
    // Prefer ranks map; callers should keep these aligned.
  }

  const nextRanks = ranksOf(ranks, nodeId) + ranksToBuy;
  ranks[nodeId] = nextRanks;
  const nextSpent = totalSpentFromRanks(ranks);

  return {
    ...cls,
    talentRanks: ranks,
    spentPoints: nextSpent,
  };
}

/**
 * Purchase as many ranks as possible up to `maxRanksWanted` in one atomic
 * transaction (capped by room and unspent). Returns unchanged cls if zero.
 */
export function purchaseTalentRanksUpTo(
  cls: ClassProgress,
  nodeId: string,
  maxRanksWanted: number,
): ClassProgress {
  if (!Number.isInteger(maxRanksWanted) || maxRanksWanted <= 0) {
    throw new TalentPurchaseError('invalid_ranks', 'maxRanksWanted must be a positive integer');
  }
  const ranks = cls.talentRanks ?? emptyTalentRanks();
  let node: TalentNodeDef;
  try {
    node = getVanguardTalentNode(nodeId);
  } catch {
    throw new TalentPurchaseError('unknown_node', `Unknown node ${nodeId}`);
  }
  const room = Math.max(0, node.maxRanks - ranksOf(ranks, nodeId));
  const unspent = availableClassTalentPoints(cls);
  const buy = Math.min(maxRanksWanted, room, unspent);
  if (buy <= 0) {
    const preview = previewTalentPurchase(cls, nodeId, 1);
    throw new TalentPurchaseError(
      (preview.rejectReason as TalentPurchaseRejectReason) ?? 'insufficient_points',
      `Cannot purchase any ranks of ${nodeId}: ${preview.rejectReason}`,
    );
  }
  return purchaseTalentRanks(cls, nodeId, buy);
}

/**
 * Respec: refund all invested class points into unspent (spentPoints → 0,
 * clear ranks). Does NOT invent currency cost — caller must gate on TBD hub cost.
 * Revalidation of tier gates is implicit (empty ranks → only General T1 open).
 */
export function respecVanguardTalents(cls: ClassProgress): ClassProgress {
  return {
    ...cls,
    talentRanks: emptyTalentRanks(),
    spentPoints: 0,
  };
}

export function applyTalentPurchaseToPermanent(
  permanent: PermanentSave,
  classId: 'vanguard',
  nodeId: string,
  ranksToBuy = 1,
): PermanentSave {
  const cls = permanent.classes[classId];
  const next = purchaseTalentRanks(cls, nodeId, ranksToBuy);
  return {
    ...permanent,
    classes: {
      ...permanent.classes,
      [classId]: next,
    },
  };
}

export function applyTalentPurchaseUpToPermanent(
  permanent: PermanentSave,
  classId: 'vanguard',
  nodeId: string,
  maxRanksWanted: number,
): PermanentSave {
  const cls = permanent.classes[classId];
  const next = purchaseTalentRanksUpTo(cls, nodeId, maxRanksWanted);
  return {
    ...permanent,
    classes: {
      ...permanent.classes,
      [classId]: next,
    },
  };
}

export function listUnlockedActiveSkillIds(ranks: TalentRankMap): string[] {
  const ids: string[] = [];
  for (const node of VANGUARD_TALENT_NODES) {
    if (node.kind !== 'active') continue;
    if (ranksOf(ranks, node.id) >= 1 && node.effect.skillId) {
      ids.push(node.effect.skillId);
    }
  }
  return ids;
}

export function describeNextRankEffect(node: TalentNodeDef, currentRanks: number): string {
  if (currentRanks >= node.maxRanks) {
    return 'Max rank reached';
  }
  if (node.kind === 'active') {
    return node.effect.description;
  }
  return node.effect.description;
}

export function accumulatedEffectValue(
  node: TalentNodeDef,
  ranks: number,
): number {
  return node.effect.perRank * Math.max(0, Math.min(node.maxRanks, ranks));
}
